import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import { generateInvoice } from "./generateInvoice";
import OrdersSkeleton from "./OrderSkelton";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const Orders = () => {
  // State variables
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [startDate, setStartDate] = useState("2025-01-01");
  const [endDate, setEndDate] = useState("2025-12-01");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Separate state for order status modal
  const [isOrderStatusModalOpen, setIsOrderStatusModalOpen] = useState(false);
  const [updatedOrderStatus, setUpdatedOrderStatus] = useState("");
  const [orderStatusUpdateLoading, setOrderStatusUpdateLoading] =
    useState(false);
  const [orderStatusUpdateError, setOrderStatusUpdateError] = useState(null);
  const [orderStatusUpdateSuccess, setOrderStatusUpdateSuccess] =
    useState(false);

  // Separate state for payment status modal
  const [isPaymentStatusModalOpen, setIsPaymentStatusModalOpen] =
    useState(false);
  const [updatedPaymentStatus, setUpdatedPaymentStatus] = useState("");
  const [paymentStatusUpdateLoading, setPaymentStatusUpdateLoading] =
    useState(false);
  const [paymentStatusUpdateError, setPaymentStatusUpdateError] =
    useState(null);
  const [paymentStatusUpdateSuccess, setPaymentStatusUpdateSuccess] =
    useState(false);

  // Fetch orders from API
  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Get token from sessionStorage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Make API call with query parameters
      const response = await axios.get(`${baseURL}/api/orders`, {
        params: {
          startDate,
          endDate,
          page,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update state with response data
      const ordersData = response.data.orders || [];

      // Parse orderItems JSON string for each order
      const processedOrders = ordersData.map((order) => {
        return {
          ...order,
          orderItems:
            typeof order.orderItems === "string"
              ? JSON.parse(order.orderItems)
              : order.orderItems,
        };
      });

      setOrders(processedOrders);

      // Calculate total pages if pagination info is available
      if (response.data.totalPages) {
        setTotalPages(response.data.totalPages);
      } else if (response.data.pagination) {
        setTotalPages(Math.ceil(response.data.pagination.total / limit));
      }

      setError(null);
    } catch (err) {
      setError(err.message || "Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders when component mounts or when dependencies change
  useEffect(() => {
    fetchOrders();
  }, [page, limit, startDate, endDate]);

  // Handle page navigation
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Handle date filter changes
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  // Open modal with order details
  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  // Order Status Modal Functions
  const openOrderStatusModal = () => {
    setUpdatedOrderStatus(selectedOrder.orderStatus || "processing");
    setOrderStatusUpdateError(null);
    setOrderStatusUpdateSuccess(false);
    setIsOrderStatusModalOpen(true);
  };

  const closeOrderStatusModal = () => {
    setIsOrderStatusModalOpen(false);
  };

  // Payment Status Modal Functions
  const openPaymentStatusModal = () => {
    setUpdatedPaymentStatus(selectedOrder.paymentStatus || "pending");
    setPaymentStatusUpdateError(null);
    setPaymentStatusUpdateSuccess(false);
    setIsPaymentStatusModalOpen(true);
  };

  const closePaymentStatusModal = () => {
    setIsPaymentStatusModalOpen(false);
  };

  // Handle update order status
  const handleUpdateOrderStatus = async () => {
    setOrderStatusUpdateLoading(true);
    setOrderStatusUpdateError(null);
    setOrderStatusUpdateSuccess(false);

    try {
      // Get token from sessionStorage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Only make API call if there are changes
      if (updatedOrderStatus !== selectedOrder.orderStatus) {
        // Make PUT API call to update order status
        await axios.put(
          `${baseURL}/api/orders/${selectedOrder.orderId}`,
          {
            field: "orderStatus",
            newStatus: updatedOrderStatus,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Update the selected order in state
        const updatedOrder = {
          ...selectedOrder,
          orderStatus: updatedOrderStatus,
        };

        setSelectedOrder(updatedOrder);

        // Update the order in the orders list
        const updatedOrders = orders.map((order) =>
          order.orderId === selectedOrder.orderId ? updatedOrder : order
        );

        setOrders(updatedOrders);
        setOrderStatusUpdateSuccess(true);

        // Close update modal after short delay
        setTimeout(() => {
          closeOrderStatusModal();
        }, 1500);
      } else {
        setOrderStatusUpdateError("No changes made to order status");
      }
    } catch (err) {
      setOrderStatusUpdateError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update order status"
      );
    } finally {
      setOrderStatusUpdateLoading(false);
    }
  };

  // Handle update payment status
  const handleUpdatePaymentStatus = async () => {
    setPaymentStatusUpdateLoading(true);
    setPaymentStatusUpdateError(null);
    setPaymentStatusUpdateSuccess(false);

    try {
      // Get token from sessionStorage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Only make API call if there are changes
      if (updatedPaymentStatus !== selectedOrder.paymentStatus) {
        // Make PUT API call to update payment status
        await axios.put(
          `${baseURL}/api/orders/${selectedOrder.orderId}`,
          {
            field: "paymentStatus",
            newStatus: updatedPaymentStatus,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Update the selected order in state
        const updatedOrder = {
          ...selectedOrder,
          paymentStatus: updatedPaymentStatus,
        };

        setSelectedOrder(updatedOrder);

        // Update the order in the orders list
        const updatedOrders = orders.map((order) =>
          order.orderId === selectedOrder.orderId ? updatedOrder : order
        );

        setOrders(updatedOrders);
        setPaymentStatusUpdateSuccess(true);

        // Close update modal after short delay
        setTimeout(() => {
          closePaymentStatusModal();
        }, 1500);
      } else {
        setPaymentStatusUpdateError("No changes made to payment status");
      }
    } catch (err) {
      setPaymentStatusUpdateError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update payment status"
      );
    } finally {
      setPaymentStatusUpdateLoading(false);
    }
  };

  // Formatting date for display
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  // Determine payment method display text
  const getPaymentMethodText = (method) => {
    switch (method) {
      case "1":
        return "ONLINE";
      case "2":
        return "COD";
      default:
        return `Payment Method ${method}`;
    }
  };

  // Order Details Modal component
  const OrderDetailsModal = () => {
    if (!selectedOrder) return null;
  };

  return (
    <div className="px-4 sm:px-4 bg-_white pt-5 rounded-md w-full border">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 text-_dark">
        Order Management
      </h1>

      {/* Date Filter Controls */}
      <div className="mb-4 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4">
        <div className="flex flex-col w-full sm:w-auto">
          <label htmlFor="startDate" className="mb-1 text-_dark text-sm">
            Start Date:
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="border border-gray-lite p-2 rounded w-full h-[40px]"
          />
        </div>

        <div className="flex flex-col w-full sm:w-auto">
          <label htmlFor="endDate" className="mb-1 text-_dark text-sm">
            End Date:
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="border border-gray-lite p-2 rounded w-full h-[40px]"
          />
        </div>

        <div className="flex flex-col w-full sm:w-auto">
          <label htmlFor="limit" className="mb-1 text-_dark text-sm">
            Items per page:
          </label>
          <select
            id="limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-gray-lite p-2 rounded w-full h-[40px]"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-_red bg-opacity-10 text-_red p-4 mb-4 rounded text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <OrdersSkeleton />
      ) : (
        <>
          {/* Orders Table */}
          {orders.length > 0 ? (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-lite rounded-lg">
                  <table className="min-w-full divide-y divide-gray-lite">
                    <thead className="bg-bg-dark text-_gray">
                      <tr>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Order ID
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Date
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Customer
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell"
                        >
                          City
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Total
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell"
                        >
                          Payment
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-2 sm:px-4 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-_white divide-y divide-gray-lite">
                      {orders.map((order) => (
                        <tr key={order.orderId} className="hover:bg-gray-hover">
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm">
                            {order.orderId}
                          </td>
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm">
                            <div className="line-clamp-2">
                              {order.firstName + " " + order.lastName}
                            </div>
                            <div className="line-clamp-1 text-xs text-_gray hidden sm:block">
                              {order.phone}
                            </div>
                          </td>
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">
                            {order.city}
                          </td>
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm">
                            LKR {order.amount}
                          </td>
                          <td className="py-2 px-2 sm:px-4 text-xs sm:text-sm hidden md:table-cell">
                            <div>
                              {getPaymentMethodText(order.paymentMethod)}
                            </div>
                            <span
                              className={`
                                text-xs py-1 px-2 rounded mt-1 inline-block
                                ${
                                  order.paymentStatus === "paid"
                                    ? "bg-_blue text-_white"
                                    : "bg-yellow-200 text-yellow-800"
                                }
                              `}
                            >
                              {order.paymentStatus}
                            </span>
                          </td>
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium
                                  ${
                                    order.orderStatus === "completed"
                                      ? "bg-_blue text-_white"
                                      : ""
                                  }
                                  ${
                                    order.orderStatus === "processing"
                                      ? "bg-_gray text-_white"
                                      : ""
                                  }
                                  ${
                                    order.orderStatus === "cancelled"
                                      ? "bg-_red text-_white"
                                      : ""
                                  }
                                  ${
                                    order.orderStatus === "posted"
                                      ? "bg-green-200 text-green-800"
                                      : ""
                                  }
                                  ${
                                    !order.orderStatus
                                      ? "bg-bg-lite text-_white"
                                      : ""
                                  }
                                `}
                            >
                              {order.orderStatus || "processing"}
                            </span>
                          </td>
                          <td className="py-2 px-2 sm:px-4 whitespace-nowrap text-xs sm:text-sm">
                            <button
                              className="bg-_blue text-_white px-2 py-1 rounded text-xs sm:text-sm hover:bg-bg-dark"
                              onClick={() => openOrderModal(order)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-hover rounded text-_dark">
              No orders found for the selected date range.
            </div>
          )}

          {/* Pagination Controls */}
          {orders.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
              <div className="text-_dark text-sm order-2 sm:order-1">
                Showing page {page} of {totalPages || 1}
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <button
                  onClick={handlePrevPage}
                  disabled={page <= 1}
                  className={`px-3 py-1 rounded text-sm flex-1 sm:flex-none ${
                    page <= 1
                      ? "bg-_gray text-_white cursor-not-allowed"
                      : "bg-_blue text-_white hover:bg-bg-dark"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={page >= totalPages}
                  className={`px-3 py-1 rounded text-sm flex-1 sm:flex-none ${
                    page >= totalPages
                      ? "bg-_gray text-_white cursor-not-allowed"
                      : "bg-_blue text-_white hover:bg-bg-dark"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-_dark bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-_white rounded-lg shadow-lg w-full max-w-4xl max-h-full overflow-auto">
            <div className="p-3 sm:p-6">
              <div className="flex justify-between items-center mb-4 border-b border-gray-lite pb-4">
                <h2 className="text-lg sm:text-xl font-bold text-_dark">
                  Order Details: #{selectedOrder.orderId}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-_gray hover:text-_dark"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-hover p-3 sm:p-4 rounded">
                  <h3 className="font-medium text-_dark mb-2 border-b border-gray-lite pb-2 text-sm sm:text-base">
                    Customer Information
                  </h3>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">Name:</span>{" "}
                    {selectedOrder.firstName} {selectedOrder.lastName}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">Phone:</span>{" "}
                    {selectedOrder.phone}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">Address:</span>{" "}
                    {selectedOrder.shippingAddress}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">City:</span>{" "}
                    {selectedOrder.city}
                  </p>
                </div>

                <div className="bg-gray-hover p-3 sm:p-4 rounded">
                  <h3 className="font-medium text-_dark mb-2 border-b border-gray-lite pb-2 text-sm sm:text-base">
                    Order Information
                  </h3>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">
                      Order Date:
                    </span>{" "}
                    {formatDate(selectedOrder.createdAt)}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">
                      Total Amount:
                    </span>{" "}
                    LKR {selectedOrder.amount}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">
                      Payment Method:
                    </span>{" "}
                    {getPaymentMethodText(selectedOrder.paymentMethod)}
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">
                      Payment Status:
                    </span>
                    <span
                      className={`ml-2 text-xs py-1 px-2 rounded 
                        ${
                          selectedOrder.paymentStatus === "paid"
                            ? "bg-_blue text-_white"
                            : "bg-yellow-200 text-yellow-800"
                        }
                      `}
                    >
                      {selectedOrder.paymentStatus}
                    </span>
                  </p>
                  <p className="mb-2 text-xs sm:text-sm">
                    <span className="font-medium text-bg-dark">
                      Order Status:
                    </span>
                    <span
                      className={`ml-2 text-xs py-1 px-2 rounded 
                        ${
                          selectedOrder.orderStatus === "completed"
                            ? "bg-_blue text-_white"
                            : ""
                        }
                        ${
                          selectedOrder.orderStatus === "processing"
                            ? "bg-yellow-200 text-yellow-800"
                            : ""
                        }
                        ${
                          selectedOrder.orderStatus === "cancelled"
                            ? "bg-_red text-_white"
                            : ""
                        }
                        ${
                          selectedOrder.orderStatus === "posted"
                            ? "bg-green-200 text-green-800"
                            : ""
                        }
                        ${
                          !selectedOrder.orderStatus
                            ? "bg-bg-lite text-_white"
                            : ""
                        }
                      `}
                    >
                      {selectedOrder.orderStatus || "processing"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-medium text-_dark mb-2 border-b border-gray-lite pb-2 text-sm sm:text-base">
                  Order Items
                </h3>
                <div className="overflow-x-auto -mx-3 sm:mx-0">
                  <table className="w-full border-collapse text-xs sm:text-sm">
                    <thead className="bg-bg-lite text-gray-500">
                      <tr>
                        <th className="py-2 px-3 border border-gray-lite text-left">
                          Product
                        </th>
                        <th className="py-2 px-3 border border-gray-lite text-left hidden sm:table-cell">
                          Sub Product
                        </th>
                        <th className="py-2 px-3 border border-gray-lite text-left">
                          Qty
                        </th>
                        <th className="py-2 px-3 border border-gray-lite text-left">
                          Price
                        </th>
                        <th className="py-2 px-3 border border-gray-lite text-left">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.orderItems &&
                        selectedOrder.orderItems.map((item) => (
                          <tr
                            key={item.orderItemId}
                            className="hover:bg-gray-hover"
                          >
                            <td className="py-2 px-3 border border-gray-lite">
                              {item.product}
                              <div className="text-xs text-_gray sm:hidden">
                                {item.title}
                              </div>
                            </td>
                            <td className="py-2 px-3 border border-gray-lite hidden sm:table-cell">
                              {item.title}
                            </td>
                            <td className="py-2 px-3 border border-gray-lite">
                              {item.count}
                            </td>
                            <td className="py-2 px-3 border border-gray-lite">
                              LKR {item.unitPrice}
                            </td>
                            <td className="py-2 px-3 border border-gray-lite">
                              LKR {item.unitPrice * item.count}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
                <button
                  onClick={closeModal}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-_gray text-_white rounded text-xs sm:text-sm"
                >
                  Close
                </button>
                <button
                  onClick={openOrderStatusModal}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-_blue text-_white rounded text-xs sm:text-sm"
                >
                  Update Status
                </button>
                <button
                  onClick={openPaymentStatusModal}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-green-600 text-_white rounded text-xs sm:text-sm"
                >
                  Update Payment
                </button>
                <button
                  className="px-3 py-1 sm:px-4 sm:py-2 text-_white rounded bg-_dark text-xs sm:text-sm"
                  onClick={() => {
                    generateInvoice(selectedOrder);
                  }}
                >
                  Print Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Status Modal */}
      {isOrderStatusModalOpen && (
        <div className="fixed inset-0 bg-_dark bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-_white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-3 sm:p-6">
              <div className="flex justify-between items-center mb-4 border-b border-gray-lite pb-4">
                <h2 className="text-lg sm:text-xl font-bold text-_dark">
                  Update Order Status
                </h2>
                <button
                  onClick={closeOrderStatusModal}
                  className="text-_gray hover:text-_dark"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {orderStatusUpdateError && (
                <div className="bg-_red bg-opacity-10 text-_red p-4 mb-4 rounded text-xs sm:text-sm">
                  {orderStatusUpdateError}
                </div>
              )}

              {orderStatusUpdateSuccess && (
                <div className="bg-_blue bg-opacity-10 text-_blue p-4 mb-4 rounded text-xs sm:text-sm">
                  Order status updated successfully!
                </div>
              )}

              <div className="mb-6">
                <label
                  htmlFor="orderStatus"
                  className="block mb-2 text-_dark text-xs sm:text-sm"
                >
                  Current Status:
                  <span
                    className={`ml-2 text-xs py-1 px-2 rounded 
                      ${
                        selectedOrder.orderStatus === "completed"
                          ? "bg-_blue text-_white"
                          : ""
                      }
                      ${
                        selectedOrder.orderStatus === "processing"
                          ? "bg-yellow-200 text-yellow-800"
                          : ""
                      }
                      ${
                        selectedOrder.orderStatus === "cancelled"
                          ? "bg-_red text-_white"
                          : ""
                      }
                      ${
                        selectedOrder.orderStatus === "posted"
                          ? "bg-green-200 text-green-800"
                          : ""
                      }
                      ${
                        !selectedOrder.orderStatus
                          ? "bg-bg-lite text-_white"
                          : ""
                      }
                    `}
                  >
                    {selectedOrder.orderStatus || "processing"}
                  </span>
                </label>
                <select
                  id="orderStatus"
                  value={updatedOrderStatus}
                  onChange={(e) => setUpdatedOrderStatus(e.target.value)}
                  className="w-full border border-gray-lite p-2 rounded text-xs sm:text-sm"
                  disabled={orderStatusUpdateLoading}
                >
                  <option value="processing">Processing</option>
                  <option value="posted">Posted</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  onClick={closeOrderStatusModal}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-_gray text-_white rounded text-xs sm:text-sm"
                  disabled={orderStatusUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateOrderStatus}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-_blue text-_white rounded flex items-center text-xs sm:text-sm"
                  disabled={orderStatusUpdateLoading}
                >
                  {orderStatusUpdateLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-_white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update Status"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Modal */}
      {isPaymentStatusModalOpen && (
        <div className="fixed inset-0 bg-_dark bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-_white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-3 sm:p-6">
              <div className="flex justify-between items-center mb-4 border-b border-gray-lite pb-4">
                <h2 className="text-lg sm:text-xl font-bold text-_dark">
                  Update Payment Status
                </h2>
                <button
                  onClick={closePaymentStatusModal}
                  className="text-_gray hover:text-_dark"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {paymentStatusUpdateError && (
                <div className="bg-_red bg-opacity-10 text-_red p-4 mb-4 rounded text-xs sm:text-sm">
                  {paymentStatusUpdateError}
                </div>
              )}

              {paymentStatusUpdateSuccess && (
                <div className="bg-_blue bg-opacity-10 text-_blue p-4 mb-4 rounded text-xs sm:text-sm">
                  Payment status updated successfully!
                </div>
              )}

              <div className="mb-6">
                <label
                  htmlFor="paymentStatus"
                  className="block mb-2 text-_dark text-xs sm:text-sm"
                >
                  Current Payment Status:
                  <span
                    className={`ml-2 text-xs py-1 px-2 rounded 
                      ${
                        selectedOrder.paymentStatus === "paid"
                          ? "bg-_blue text-_white"
                          : "bg-yellow-200 text-yellow-800"
                      }
                    `}
                  >
                    {selectedOrder.paymentStatus}
                  </span>
                </label>
                <select
                  id="paymentStatus"
                  value={updatedPaymentStatus}
                  onChange={(e) => setUpdatedPaymentStatus(e.target.value)}
                  className="w-full border border-gray-lite p-2 rounded text-xs sm:text-sm"
                  disabled={paymentStatusUpdateLoading}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  onClick={closePaymentStatusModal}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-_gray text-_white rounded text-xs sm:text-sm"
                  disabled={paymentStatusUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePaymentStatus}
                  className="px-3 py-1 sm:px-4 sm:py-2 bg-green-600 text-_white rounded flex items-center text-xs sm:text-sm"
                  disabled={paymentStatusUpdateLoading}
                >
                  {paymentStatusUpdateLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-_white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update Payment"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
